import { useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  sanitizeClientId,
  sanitizeDeviceType,
  sanitizeResourceId,
  sanitizeRouteToken,
  sanitizeRouteSlug,
  sanitizeUsername,
} from 'src/utils/sanitize';

export type RouteParamSanitizer = (value: string) => string;

const ROUTE_PARAM_SANITIZERS: Record<string, RouteParamSanitizer> = {
  id: sanitizeResourceId,
  campaignId: sanitizeResourceId,
  jobId: sanitizeResourceId,
  username: sanitizeUsername,
  tab: sanitizeRouteSlug,
  type: sanitizeDeviceType,
  token: sanitizeRouteToken,
};

/** Use when admin client route `:id` must allow client slug characters. */
export const ADMIN_CLIENT_ID_PARAM_OVERRIDES = { id: sanitizeClientId };

const sanitizeRouteParam = (
  key: string,
  value: string,
  overrides?: Partial<Record<string, RouteParamSanitizer>>,
): string | undefined => {
  const sanitizer = overrides?.[key] ?? ROUTE_PARAM_SANITIZERS[key] ?? sanitizeResourceId;
  const sanitized = sanitizer(value);
  return sanitized || undefined;
};

/**
 * Returns route params sanitized at the trust boundary to prevent DOM XSS from malicious URLs.
 */
export const useSanitizedRouteParams = (
  overrides?: Partial<Record<string, RouteParamSanitizer>>,
): Record<string, string | undefined> => {
  const params = useParams();

  return useMemo(() => {
    const result: Record<string, string | undefined> = {};

    for (const [key, value] of Object.entries(params)) {
      result[key] = value ? sanitizeRouteParam(key, value, overrides) : undefined;
    }

    return result;
  }, [params, overrides]);
};

/**
 * Returns a single sanitized query string parameter.
 */
export const useSanitizedSearchParam = (
  name: string,
  sanitizer: RouteParamSanitizer = sanitizeResourceId,
): string | undefined => {
  const [searchParams] = useSearchParams();
  const value = searchParams.get(name);

  return useMemo(() => {
    if (!value) {
      return undefined;
    }
    const sanitized = sanitizer(value);
    return sanitized || undefined;
  }, [name, sanitizer, value]);
};
